import {
  Notice,
  NoticeCreateRequestDTO,
  NoticeUpdateExpirationRequestDTO,
  useCreateNoticeMutation,
  useDeleteNoticeMutation,
  useUpdateNoticeExpirationMutation,
} from "@ots-frontend/ots-data";
import {useCallback, useEffect} from "react";
import {notifications} from "@mantine/notifications";
import {ResultTypeFrom} from "@reduxjs/toolkit/query";
import {useTranslation} from "react-i18next";
import {useDebouncedState} from "@mantine/hooks";
import {modals} from "@mantine/modals";
import {Text} from "@mantine/core";
import {EntityMutationCallback} from "./useEntityMutation";

export type UseNoticeMutationProps = {}

export type UseNoticeMutationResult = {
  createNotice: (notice: NoticeCreateRequestDTO, callback: EntityMutationCallback<Notice> | undefined) => void
  updateNotice: (notice: { id: string, dto: NoticeUpdateExpirationRequestDTO }, callback: EntityMutationCallback<Notice> | undefined) => void
  deleteNotice: (noticeId: string, callback: EntityMutationCallback | undefined) => void

  isCreating?: boolean
  isUpdating?: boolean
  isDeleting?: boolean
}

export const useNoticeMutation = (props: UseNoticeMutationProps = {}): UseNoticeMutationResult => {
  const {t} = useTranslation(["common"])

  // state
  const [_creating, _setCreating] = useDebouncedState(false, 500)
  const [_updating, _setUpdating] = useDebouncedState(false, 500)
  const [_deleting, _setDeleting] = useDebouncedState(false, 500)

  // possible mutations
  const [_createNotice, {isLoading: isCreating}] = useCreateNoticeMutation()
  const [_updateNotice, {isLoading: isUpdating}] = useUpdateNoticeExpirationMutation()
  const [_deleteNotice, {isLoading: isDeleting}] = useDeleteNoticeMutation()

  useEffect(() => {
    _setCreating(isCreating)
  }, [isCreating, _setCreating])

  useEffect(() => {
    _setUpdating(isUpdating)
  }, [isUpdating, _setUpdating])

  useEffect(() => {
    _setDeleting(isDeleting)
  }, [isDeleting, _setDeleting])

  useEffect(() => {
    if (_creating) {
      notifications.show({
        id: "useNoticeUpdate_creating",
        title: t("common.status.creating", "Creating") + " " + t("common.entity.notice", "Notice"),
        message: "",
        loading: true,
        autoClose: 5000,
      });
    } else {
      notifications.hide("useNoticeUpdate_creating")
    }
  }, [_creating, t]);

  useEffect(() => {
    if (_updating) {
      notifications.show({
        id: "useNoticeUpdate_updating",
        title: t("common.status.updating", "Updating") + " " + t("common.entity.notice", "Notice"),
        message: "",
        loading: true,
        autoClose: 5000,
      });
    } else {
      notifications.hide("useNoticeUpdate_updating")
    }
  }, [_updating, t]);

  useEffect(() => {
    if (_deleting) {
      notifications.show({
        id: "useNoticeUpdate_deleting",
        title: t("common.status.deleting", "Deleting") + " " + t("common.entity.notice", "Notice"),
        message: "",
        loading: true,
        autoClose: 5000,
      });
    } else {
      notifications.hide("useNoticeUpdate_deleting")
    }
  }, [_deleting, t]);

  const handleCreateResult = useCallback((promise: Promise<Notice>, callback: EntityMutationCallback<Notice> | undefined) => {
    promise
      .then((payload) => {
        notifications.show({
          color: "green",
          title: t("common.entity.notice", "Notice") + " " + t("common.status.created", "Created"),
          message: ""
        });
        if (callback?.onSuccess) {
          callback.onSuccess(payload)
        }
      })
      .catch((rejected) => {
          notifications.show({
            color: "red",
            title: t("common.error", "Error"),
            message: rejected.data?.detail ?? t("common.error", "Error"),
          })

          if (callback?.onFailure) {
            callback.onFailure()
          }
        }
      );
  }, [t])

  const handleUpdateResult = useCallback((promise: Promise<Notice>, callback: EntityMutationCallback<Notice> | undefined) => {
    promise
      .then((payload) => {
        notifications.show({
          color: "green",
          title: t("common.entity.notice", "Notice") + " " + t("common.status.updated", "Updated"),
          message: ""
        });
        if (callback?.onSuccess) {
          callback.onSuccess(payload)
        }
      })
      .catch((rejected) => {
          notifications.show({
            color: "red",
            title: t("common.error", "Error"),
            message: rejected.data?.detail ?? t("common.error", "Error"),
          })

          if (callback?.onFailure) {
            callback.onFailure()
          }
        }
      );
  }, [t])

  const handleDeleteResult = useCallback((promise: Promise<ResultTypeFrom<any>>, callback: EntityMutationCallback | undefined) => {
    promise
      .then(() => {
        notifications.show({
          color: "green",
          title: t("common.entity.notice", "Notice") + " " + t("common.status.deleted", "Deleted"),
          message: ""
        });

        if (callback?.onSuccess) {
          callback.onSuccess(undefined)
        }
      })
      .catch((rejected) => {
          notifications.show({
            color: "red",
            title: t("common.error", "Error"),
            message: rejected.data?.detail ?? t("common.error", "Error"),
          })

          if (callback?.onFailure) {
            callback.onFailure()
          }
        }
      );
  }, [t])

  const handleDeleteConfirm = useCallback((noticeId: string, callback: EntityMutationCallback<Notice> | undefined) => {
    const promise = _deleteNotice({id: noticeId}).unwrap()
    handleDeleteResult(promise, callback)
  }, [_deleteNotice, handleDeleteResult])

  const createNotice = useCallback((notice: NoticeCreateRequestDTO, callback: EntityMutationCallback<Notice> | undefined) => {
    const promise = _createNotice(notice).unwrap()
    handleCreateResult(promise, callback)
  }, [_createNotice, handleCreateResult])

  const updateNotice = useCallback((notice: { id: string, dto: NoticeUpdateExpirationRequestDTO }, callback: EntityMutationCallback | undefined) => {
    const promise = _updateNotice(notice).unwrap()
    handleUpdateResult(promise, callback)
  }, [_updateNotice, handleUpdateResult])

  const deleteNotice = useCallback((noticeId: string, callback: EntityMutationCallback | undefined) => {
    modals.openConfirmModal({
      title: t("common.action.delete", "Delete") + " " + t("common.entity.notice", "Notice"),
      centered: true,
      children: (
        <Text size="sm" p="md">
          {t("common.action.delete.notice.confirm", "Are you sure you want to delete this notice?")}
        </Text>
      ),
      labels: {
        confirm: t("common.action.delete", "Delete"),
        cancel: t("common.action.cancel", "Cancel"),
      },
      confirmProps: {color: 'red'},
      onConfirm: () => {
        handleDeleteConfirm(noticeId, callback)
      },
    })
  }, [handleDeleteConfirm, t])

  return {
    createNotice: createNotice,
    updateNotice: updateNotice,
    deleteNotice: deleteNotice,
    isCreating: _creating,
    isUpdating: _updating,
    isDeleting: _deleting,
  }
}
